import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { ArrowLeft, Activity, Calendar, Flame, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StudentCard } from '@/components/students/student-card';
import { StudentTeachersCard } from '@/components/students/student-teachers-card';
import { ActivityTimeline } from '@/components/students/activity-timeline';
import { StudentTagsCard } from '@/components/students/student-tags-card';
import { AssignPracticeDialog } from '@/components/assignments/assign-practice-dialog';
import { NotesList } from '@/components/notes/notes-list';
import { CreateNoteForm } from '@/components/notes/create-note-form';
import { KpiCard } from '@/components/analytics/kpi-card';
import { TimeRangeTabs } from '@/components/analytics/time-range-tabs';
import { ActivityLineChart } from '@/components/analytics/activity-line-chart';
import { ActivityHeatmap } from '@/components/analytics/activity-heatmap';
import { MoodTrendChart } from '@/components/analytics/mood-trend-chart';
import { CategoryDistributionChart } from '@/components/analytics/category-distribution-chart';
import { UpcomingLoadChart } from '@/components/analytics/upcoming-load-chart';
import { useStudentDetail } from '@/hooks/use-dashboard';
import { useStudentAnalytics } from '@/hooks/use-analytics';
import { useStudentPersonalizedPractices, usePracticeSessions } from '@/hooks/use-seeds';
import { useCreateNote, useDeleteNote } from '@/hooks/use-notes';
import {
  formatDate,
  formatDateTime,
  formatSessionStatus,
} from '@/lib/utils';
import { formatPercentRate } from '@/lib/analytics-format';
import { SessionsTable } from '@/components/sessions/sessions-table';
import { isAxiosError } from 'axios';
import type { RangeParam } from '@/types/analytics';

const statusVariant: Record<string, 'default' | 'secondary' | 'destructive'> = {
  completed: 'default',
  pending: 'secondary',
  expired: 'destructive',
};

export default function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [range, setRange] = useState<RangeParam>('30d');
  const { data, isLoading, error } = useStudentDetail(id!);
  const { data: analytics, isLoading: analyticsLoading } = useStudentAnalytics(id!, range);
  const { data: aiPractices } = useStudentPersonalizedPractices(id!);
  const { data: recentSessions, isLoading: sessionsLoading } = usePracticeSessions({
    student_id: id,
    per_page: 10,
    sort_order: 'desc',
  });
  const createNote = useCreateNote();
  const deleteNote = useDeleteNote(id!);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (error) {
    const message =
      isAxiosError(error) && error.response?.data?.error?.message
        ? error.response.data.error.message
        : 'Student not found';
    return (
      <div className="space-y-4">
        <Button variant="ghost" render={<Link to="/students" />}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Students
        </Button>
        <p className="text-destructive">{message}</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <Button variant="ghost" render={<Link to="/students" />}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Students
      </Button>

      <StudentCard profile={data.profile} sessionStats={data.session_stats} />

      <StudentTagsCard studentId={data.profile.id} />

      <StudentTeachersCard studentId={data.profile.id} teachers={data.teachers ?? []} />

      <Tabs defaultValue="activity">
        <TabsList>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="ai-practices">AI Practices</TabsTrigger>
          <TabsTrigger value="reflections">Reflections</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <TimeRangeTabs value={range} onChange={setRange} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Completion rate"
              value={analytics ? formatPercentRate(analytics.kpis.completion_rate) : '—'}
              subtitle={`${analytics?.kpis.sessions_done ?? 0} completed / ${analytics?.kpis.sessions_skipped ?? 0} skipped`}
              icon={Target}
              isLoading={analyticsLoading}
            />
            <KpiCard
              title="Sessions / week"
              value={analytics ? analytics.kpis.avg_sessions_per_week.toFixed(1) : '—'}
              subtitle={`${analytics?.kpis.days_active ?? 0} active days in range`}
              icon={Activity}
              isLoading={analyticsLoading}
            />
            <KpiCard
              title="Current streak"
              value={analytics ? `${analytics.kpis.current_streak}d` : '—'}
              subtitle={`longest ${analytics?.kpis.longest_streak ?? 0}d`}
              icon={Flame}
              isLoading={analyticsLoading}
            />
            <KpiCard
              title="Days inactive"
              value={
                analytics
                  ? analytics.kpis.days_inactive < 0
                    ? 'Never active'
                    : `${analytics.kpis.days_inactive}d`
                  : '—'
              }
              subtitle={`${analytics?.kpis.pending_assignments ?? 0} pending, ${analytics?.kpis.overdue_assignments ?? 0} overdue`}
              icon={Calendar}
              isLoading={analyticsLoading}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>90-day activity</CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <Skeleton className="h-[120px] w-full" />
              ) : (
                <ActivityHeatmap data={analytics?.heatmap ?? []} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Session activity</CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <Skeleton className="h-[240px] w-full" />
              ) : (
                <ActivityLineChart data={analytics?.daily_activity ?? []} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {sessionsLoading ? (
                <Skeleton className="h-[160px] w-full" />
              ) : (
                <SessionsTable
                  sessions={recentSessions?.sessions ?? []}
                  hideStudent
                />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Mood trend</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsLoading ? (
                  <Skeleton className="h-[200px] w-full" />
                ) : (
                  <MoodTrendChart data={analytics?.mood_series ?? []} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsLoading ? (
                  <Skeleton className="h-[240px] w-full" />
                ) : (
                  <CategoryDistributionChart
                    data={analytics?.category_distribution ?? []}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Upcoming calendar (14d)</CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : (
                <UpcomingLoadChart data={analytics?.upcoming_load ?? []} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <AssignPracticeDialog
              fixedStudent={{
                id: data.profile.id,
                first_name: data.profile.first_name,
                last_name: data.profile.last_name,
                username: data.profile.username,
                email: data.profile.email,
              }}
              triggerLabel="Assign Practice"
              triggerSize="sm"
            />
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Practice</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No assignments.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.assignments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.practice_title}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant[a.status] ?? 'secondary'}>
                          {a.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(a.assigned_at)}</TableCell>
                      <TableCell>{a.due_date ? formatDate(a.due_date) : '—'}</TableCell>
                      <TableCell>{a.completed_at ? formatDate(a.completed_at) : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="ai-practices" className="mt-4">
          {!(aiPractices?.practices ?? []).length ? (
            <p className="text-sm text-muted-foreground">No AI-generated practices yet.</p>
          ) : (
            <div className="space-y-3">
              {(aiPractices?.practices ?? []).map((p) => {
                const statusLine: string[] = [];
                if (p.sessions_done > 0) statusLine.push(`✅ ${p.sessions_done} completed`);
                if (p.sessions_skipped > 0) statusLine.push(`⏭ ${p.sessions_skipped} skipped`);
                if (p.last_session_at) {
                  statusLine.push(
                    `last: ${formatSessionStatus(p.last_session_status ?? '')} on ${formatDateTime(p.last_session_at)}`,
                  );
                }
                return (
                  <Card key={p.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium">{p.title}</p>
                            {p.problem_category && (
                              <Badge variant="outline" title={p.problem_text ?? undefined}>
                                {p.problem_category}
                              </Badge>
                            )}
                            {!p.is_active && (
                              <Badge variant="secondary" className="text-muted-foreground">
                                Archived
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm">{p.description}</p>
                          {p.personalization && (
                            <p className="text-xs italic text-muted-foreground">
                              {p.personalization}
                            </p>
                          )}
                          {statusLine.length > 0 ? (
                            <p className="text-xs font-medium text-foreground">
                              {statusLine.join(' · ')}
                            </p>
                          ) : (
                            <p className="text-xs italic text-muted-foreground">
                              No sessions yet
                            </p>
                          )}
                          <div className="flex gap-3 text-xs text-muted-foreground">
                            <span>{p.duration} min</span>
                            <span>Difficulty {p.difficulty}/5</span>
                          </div>
                        </div>
                        <span className="whitespace-nowrap text-xs text-muted-foreground">
                          Generated {formatDate(p.created_at)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reflections" className="mt-4">
          {data.reflections.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reflections yet.</p>
          ) : (
            <div className="space-y-3">
              {data.reflections.map((r) => (
                <Card key={r.session_id}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{r.practice_title}</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm">{r.notes}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(r.session_date)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" className="mt-4 space-y-4">
          <CreateNoteForm
            onSubmit={(body) => createNote.mutate({ student_id: id!, body })}
            isPending={createNote.isPending}
          />
          <NotesList
            notes={data.notes}
            onDelete={(noteId) => deleteNote.mutate(noteId)}
            isDeleting={deleteNote.isPending}
          />
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <ActivityTimeline studentId={id!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
